import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

// console.log('PaymentSuccess module loaded');

// Constants
const MAX_RETRIES = 3;
const RETRY_BASE_DELAY = 500; // 500ms base delay for exponential backoff
const REDIRECT_DELAY = 2000;

// Status types for better type safety
const STATUS = {
  LOADING: 'loading',
  VERIFYING: 'verifying',
  VERIFIED: 'verified',
  NEEDS_AUTH: 'needsAuth',
  SUCCESS: 'success',
  ERROR: 'error'
};

export default function PaymentSuccess() {
  // console.log('PaymentSuccess component rendered');

  const { 
    user, 
    ready,
    profile,
    refreshUser, 
    setVerifyingPayment 
  } = useAuth();
  
  // Don't track isLoading from AuthContext to avoid flickers during refreshUser
  // PaymentSuccess manages its own loading states
  
  const navigate = useNavigate();
  const location = useLocation();
  
  // Core state
  const [status, setStatus] = useState(STATUS.LOADING);
  const [error, setError] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  // Ref to prevent re-entrant or concurrent execution of handleAuth logic
  const isAuthLogicRunning = useRef(false);

  // URL params
  const sessionId = new URLSearchParams(location.search).get("session_id");
  // Force dashboard redirect after payment success, ignoring return_path
  const returnPath = "/dashboard";

  // Derived state - more stable conditions to reduce flickers
  const isUserReady = user && typeof user === 'object';
  const isCompleted = [STATUS.SUCCESS, STATUS.ERROR, STATUS.NEEDS_AUTH].includes(status);
  const canVerify = !isCompleted && isUserReady && sessionId && status === STATUS.LOADING && !sessionData;
  const canHandleAuth = status === STATUS.VERIFIED && sessionData && !isCompleted;
  
  // Prevent re-triggers during auth loading states
  const authStable = ready;

  // Debug logging
  useEffect(() => {
    // console.log('🔍 State updated:', {
    //   status,
    //   user: user === null ? 'loading' : user === false ? 'not_authenticated' : 'authenticated',
    //   ready,
    //   hasProfile: !!profile,
    //   isCompleted,
    //   canVerify,
    //   canHandleAuth,
    //   retryCount,
    //   sessionData: sessionData ? 'present' : 'null',
    //   returnPath
    // });

    // Log detailed verification conditions
    // console.log('🔍 Verification conditions:', {
    //   notCompleted: !isCompleted,
    //   userReady: isUserReady,
    //   hasSessionId: !!sessionId,
    //   isLoading: status === STATUS.LOADING,
    //   noSessionData: !sessionData,
    //   canVerify
    // });

    // Log detailed auth conditions
    // console.log('🔍 Auth conditions:', {
    //   isVerified: status === STATUS.VERIFIED,
    //   hasSessionData: !!sessionData,
    //   notCompleted: !isCompleted,
    //   canHandleAuth
    // });
      }, [status, user, ready, profile, isCompleted, canVerify, canHandleAuth, retryCount, sessionData, returnPath, isUserReady, sessionId]);

  // Helper function for retry delay
  const retryDelay = useCallback(async (attempt) => {
    const delay = Math.min(2 ** attempt * RETRY_BASE_DELAY, 5000); // Cap at 5 seconds
    await new Promise(resolve => setTimeout(resolve, delay));
  }, []);

  // Verify payment
  const verifyPayment = useCallback(async () => {
    // console.log('🔄 Verify payment called:', { canVerify, sessionId, userId: user?.id });
    
    if (!canVerify) {
      // console.log('❌ Skipping verification:', {
      //   reason: !isUserReady ? 'user not ready' :
      //          !sessionId ? 'no session ID' :
      //          status !== STATUS.LOADING ? 'wrong status' :
      //          sessionData ? 'already verified' :
      //          isCompleted ? 'already completed' :
      //          'unknown'
      // });
      return;
    }

    try {
      // console.log('✨ Starting payment verification...');
      setStatus(STATUS.VERIFYING);
      setVerifyingPayment(true);

      // console.log('Starting payment verification...', {
      //   sessionId,
      //   userId: user.id,
      //   returnPath
      // });

      const verifyUrl = `${window.location.origin}/api/verify-session`;
      // console.log('Calling verify-session endpoint:', verifyUrl);
      
      const response = await fetch(verifyUrl, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ 
          sessionId,
          userId: user.id
        })
      });

      // console.log('Verify-session response status:', response.status);
      const data = await response.json();
      // console.log('Verify-session response data:', data);
      
      if (!response.ok) {
        throw new Error(data.error || 'Payment verification failed');
      }

      // console.log('✅ Payment verified successfully:', { sessionData: data });
      setSessionData(data);
      setStatus(STATUS.VERIFIED);

    } catch (err) {
      console.error('❌ Verification failed:', err);
      setError(err.message);
      setStatus(STATUS.ERROR);
    } finally {
      setVerifyingPayment(false);
    }
  }, [canVerify, authStable, sessionId, user, returnPath, isUserReady, status, sessionData, isCompleted]);
  // Note: setVerifyingPayment intentionally excluded from deps as it's stable

  // Handle auth state and redirects
  const handleAuth = useCallback(async () => {
    // console.log('🔑 handleAuth instance called.', { currentStatus: status, canHandleAuthEval: canHandleAuth, isRunningRef: isAuthLogicRunning.current });

    if (!canHandleAuth) {
      // console.log('❌ handleAuth: canHandleAuth is false, exiting.');
      // If canHandleAuth became false (e.g. success/error), ensure the lock is reset.
      // This handles cases where the component might re-evaluate canHandleAuth later.
      isAuthLogicRunning.current = false;
      return;
    }

    if (isAuthLogicRunning.current) {
      // console.log('🚧 handleAuth: Logic is already running, skipping concurrent execution.');
      return;
    }

    isAuthLogicRunning.current = true;
    // console.log('🔒 handleAuth: Acquired lock.');

    try {
      // Verify correct user
      if (user.id !== sessionData.userId) {
        // console.log('❌ User ID mismatch:', { 
        //   currentUser: user.id, 
        //   expectedUser: sessionData.userId 
        // });
        setError('Please sign in with the account used for payment');
        setStatus(STATUS.NEEDS_AUTH);
        return;
      }

      // console.log('🔄 Refreshing user data...', {
      //   attempt: retryCount + 1,
      //   maxRetries: MAX_RETRIES,
      //   returnPath
      // });
      
      // Try to refresh with exponential backoff
      const { success, error: refreshError } = await refreshUser({ force: true });
      
      if (!success) {
        if (retryCount < MAX_RETRIES - 1) {
          // console.log('⚠️ Refresh failed, retrying...', {
          //   attempt: retryCount + 1,
          //   delay: Math.min(2 ** retryCount * RETRY_BASE_DELAY, 5000)
          // });
          await retryDelay(retryCount);
          setRetryCount(prev => prev + 1);
          // Lock will be released in finally block, allowing the new handleAuth instance (triggered by retryCount change) to run.
          return; 
        }
        console.error('❌ Refresh user data failed after retries:', refreshError);
        throw refreshError || new Error('Failed to refresh user data after retries');
      }

      // console.log('✅ Successfully refreshed user data:', {
      //   returnPath,
      //   redirectDelay: REDIRECT_DELAY
      // });
      setStatus(STATUS.SUCCESS);
      
      // Schedule redirect
      setTimeout(() => {
        // console.log('🚀 Executing redirect to:', returnPath);
        navigate(returnPath, { replace: true });
      }, REDIRECT_DELAY);

    } catch (err) {
      console.error('❌ Error in handleAuth logic:', err);
      setError(err.message);
      setStatus(STATUS.ERROR);
    } finally {
      // Release the lock. If status is now SUCCESS/ERROR, canHandleAuth will be false, preventing re-runs.
      // If it was a retry, this allows the next attempt to acquire the lock.
      // console.log('🔑 handleAuth: Releasing lock. Final status for this run (might change on next render):', status);
      isAuthLogicRunning.current = false;
    }
  }, [canHandleAuth, user, sessionData, retryCount, returnPath, navigate, retryDelay, status]);
  // Note: refreshUser intentionally excluded from deps to prevent infinite loops

  // Effect for verification
  useEffect(() => {
    // console.log('🔄 Verification effect running');
    verifyPayment();
  }, [verifyPayment]);

  // Effect for auth handling
  useEffect(() => {
    // console.log('🔄 Auth handling effect running');
    handleAuth();
  }, [handleAuth]);

  // Render different UI states based on status
  const renderContent = () => {
    switch (status) {
      case STATUS.LOADING:
        return (
          <>
            <h1 className="text-2xl font-bold mb-2">Checking payment status...</h1>
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          </>
        );
      
      case STATUS.VERIFYING:
        return (
          <>
            <h1 className="text-2xl font-bold mb-2">Verifying your payment...</h1>
            <div className="flex justify-center mb-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
            <p className="text-sm text-gray-400">This will only take a moment.</p>
          </>
        );

      case STATUS.VERIFIED:
        return (
          <>
            <div className="flex justify-center mb-4">
              <svg className="w-16 h-16 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold mb-2">Payment Verified! 🎉</h1>
            <p className="text-gray-300">
              {retryCount > 0 ? `Retrying... (attempt ${retryCount + 1})` : 'Checking your account...'}
            </p>
          </>
        );

      case STATUS.NEEDS_AUTH:
        return (
          <>
            <div className="flex justify-center mb-4">
              <svg className="w-16 h-16 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m0 0v2m0-2h2m-2 0H8m4-6V4" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold mb-2">Payment Successful!</h1>
            <p className="text-gray-300 mb-4">Please sign in to access your subscription.</p>
            <button
              onClick={() => navigate(`/signin?redirect=${encodeURIComponent(location.pathname + location.search)}`, { replace: true })}
              className="bg-primary hover:bg-primary-dark text-white font-bold py-2 px-4 rounded w-full"
            >
              Sign In to Continue
            </button>
            {error && (
              <p className="text-red-400 text-sm mt-4">{error}</p>
            )}
          </>
        );
      
      case STATUS.SUCCESS:
        return (
          <>
            <div className="flex justify-center mb-4">
              <svg className="w-16 h-16 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold mb-2">All Set! 🎉</h1>
            <p className="text-gray-300">Redirecting to dashboard...</p>
          </>
        );
      
      case STATUS.ERROR:
        return (
          <>
            <div className="flex justify-center mb-4">
              <svg className="w-16 h-16 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold mb-2">Oops! Something went wrong</h1>
            <p className="text-red-400 mb-4">{error}</p>
            <div className="space-y-4">
              <button
                onClick={() => window.location.reload()}
                className="bg-primary hover:bg-primary-dark text-white font-bold py-2 px-4 rounded w-full"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate('/payment')}
                className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded w-full"
              >
                Return to Payment
              </button>
              <p className="text-sm text-gray-400 mt-4">
                If the problem persists, please contact{' '}
                <a href="mailto:support@example.com" className="text-primary hover:text-primary-light">
                  support@example.com
                </a>
              </p>
            </div>
          </>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white p-4">
      <div className="w-full max-w-md text-center">
        {renderContent()}
      </div>
    </div>
  );
}